import React, { useEffect, useState } from 'react';
import { Typography, List, Row, Col, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import TemplateTile from './TemplateTile';
import dayjs from 'dayjs';
import { now } from 'moment';
import { useNavigate } from 'react-router-dom';
import { setCurrentRoute } from '../../../slices/sidebarSlice';
import { useDispatch } from 'react-redux';
import InProgressIcon from '../../../Icons/InProgressIcon';
import InProgressIconSmall from '../../../Icons/InProgressIconSmall';

const { Title, Text } = Typography;

const EventsTile = ({ data, locationData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [pastEventsCount, setPastEventsCount] = useState(0);
  const [futureEventsCount, setFutureEventsCount] = useState(0);
  const [activeEventsCount, setActiveEventsCount] = useState(0);

  useEffect(() => {
    if (data) {
      const now = dayjs();

      const pastEvents = data.filter(event =>
        dayjs(event.endTime).isBefore(now)
      );
      setPastEventsCount(pastEvents.length);

      const futureEvents = data.filter(event =>
        dayjs(event.startTime).isAfter(now)
      );
      setFutureEventsCount(futureEvents.length);

      const activeEvents = data.filter(event => {
        const eventStart = dayjs(event.startTime);
        const eventEnd = dayjs(event.endTime);
        return eventStart.isBefore(now) && eventEnd.isAfter(now);
      });
      setActiveEventsCount(activeEvents.length);
    }
  }, [data]);

  const handleListItemClick = (event) => {
      dispatch(setCurrentRoute('events'));
      navigate(`/events`);
  };

  const handleEventMatchingClick = (event) => {
    dispatch(setCurrentRoute('events'));
    navigate('/events/matchings', {
      state: {
        event: event
      }
    });
  }

  return (
    <TemplateTile
      height="43vh"
      title="Events"
      content={
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px',
              gap: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Bevorstehend" >
                <ClockCircleOutlined
                  style={{ fontSize: '24px', marginRight: '8px', color: '#F69300' }}
                />
              </Tooltip>
              <Title level={1} style={{ margin: 0 }}>
                {futureEventsCount}
              </Title>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Aktiv">
                <div style={{ display: 'flex', alignItems: 'center'}}>
                  <InProgressIcon
                    style={{ fontSize: '24px'}}
                  />
                </div>
              </Tooltip>
              <Title level={1} style={{ margin: 0 }}>
                {activeEventsCount}
              </Title>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Beendet" >
                <CheckCircleOutlined
                  style={{ fontSize: '24px', marginRight: '8px', color: '#989898' }}
                />
              </Tooltip>
              <Title level={1} style={{ margin: 0 }}>
                {pastEventsCount}
              </Title>
            </div>
          </div>
          <div
            style={{
              maxHeight: data.length > 4 ? '30vh' : 'auto',
              overflowY: data.length > 4 ? 'auto' : 'visible'
            }}
          >
            <List
              size="small"
              header={
                <Row>
                <Col span={4}>
                    <Text strong style={{marginLeft: 4}}>Eventname</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong style={{marginLeft: 8}}>Startzeit</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong>Endzeit</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong style={{marginLeft: -8}}>Status</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong style={{marginLeft: -8}}>Scanfortschritt</Text>
                  </Col>
                </Row>
              }
              dataSource={[...data]
                .filter(d => {
                  const startDate = new Date(d.startTime);
                  const endDate = new Date(d.endTime);
                  const sevenDaysAhead = new Date();
                  const today = new Date();

                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  sevenDaysAhead.setDate(today.getDate() + 7);
                  today.setHours(0, 0, 0, 0);

                  return (startDate <= today && endDate >= today) || (startDate >= today && startDate <= sevenDaysAhead);
                })
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
              }
              renderItem={event => (
                <List.Item
                  onClick={() => handleListItemClick(event)}
                  style={{cursor: 'pointer'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#FAFAFA';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                  }}
                >
                  <Row style={{ width: '100%' }}>
                    <Col span={4}>
                      <Text style={{ fontSize: 16, marginLeft: -12}}>
                        {event.name}
                      </Text>
                    </Col>
                    <Col span={4}>
                      <Text style={{marginLeft: -2}} type="secondary">
                        {dayjs(event.startTime).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    </Col>
                    <Col span={4}>
                      <Text style={{marginLeft: -4}} type="secondary">
                        {dayjs(event.endTime).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    </Col>
                    <Col span={4}>
                      {dayjs(now()).isBefore(dayjs(event.startTime)) &&
                        <>
                          <ClockCircleOutlined style={{ fontSize: '12px', marginRight: '8px', color: '#F69300'}} />
                          <Text style={{color: "orange", marginLeft: -6}} type="secondary">Bevorstehend</Text>
                        </>
                      }
                      {dayjs(now()).isBefore(dayjs(event.endTime)) && dayjs(now()).isAfter(dayjs(event.startTime)) &&
                        <>
                          <InProgressIconSmall style={{ fontSize: '12px', marginRight: '8px'}} />
                          <Text style={{color: "green", marginLeft: 0}} type="secondary">Aktiv</Text>
                        </>

                      }
                      {dayjs(now()).isAfter(dayjs(event.endTime)) &&
                        <>
                          <CheckCircleOutlined style={{ fontSize: '12px', marginRight: '8px', color: '#989898'}} />
                          <Text style={{color: "black", marginLeft: -6}} type="secondary">Beendet</Text>
                        </>
                      }
                    </Col>
                    <Col
                      span={4}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventMatchingClick(event)
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.fontWeight = 'bold';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.fontWeight = 'normal';
                      }}
                      style={{ cursor: 'pointer', transition: 'font-weight 0.1s ease' }}
                      >
                      {(() => {
                        const matchedShapesCount =
                          event?.guestList?.filter(guest => guest.matching.matched).length || 0;
                        return (
                          <>
                            <Text style={{ color: 'black', fontWeight: 'bold' }} type="secondary">
                              Verknüpft: {matchedShapesCount} / {event?.guestList?.length}
                            </Text>
                            {matchedShapesCount !== 0 && matchedShapesCount === event?.guestList?.length &&
                              <Tooltip title="Alle Stühle verknüpft.">
                                <CheckCircleOutlined
                                  style={{ fontSize: '12px', color: '#4CAF50', margin: '0 8px' }}
                                />
                              </Tooltip>
                            }
                            {matchedShapesCount > 0 && matchedShapesCount < event?.guestList?.length &&
                              <Tooltip title="Nicht alle Stühle verknüpft.">
                                <ExclamationCircleOutlined
                                  style={{ fontSize: '12px', color: '#FF9800', margin: '0 8px' }}
                                />
                              </Tooltip>
                            }
                            {matchedShapesCount === 0 &&
                              <Tooltip title="Keine Stühle verknüpft!">
                                <CloseCircleOutlined
                                  style={{ fontSize: '12px', color: '#F44336', margin: '0 8px' }}
                                />
                              </Tooltip>
                            }
                          </>
                        );
                      })()}
                    </Col>
                  </Row>
                </List.Item>
              )}
            />
          </div>
        </div>
      }
    />
  );
};

export default EventsTile;
